<template>
    <page :busy="busy" :tabs="tabs" class="policy-page">
        <div class="policy-list">
            <grid
                ref="grid"
                :columnDefs="columnDefs"
                :fetchCallback="fetchCallback"
                :suppressScrollOnNewData="true"
                :isAutoHeight="false"
                :groupDefaultExpanded="null"
            >
                <div v-if="policiesTemplates.length">
                    <div class="policy-toolbar mb-3 mr-1">
                        <form-select
                            :options="policiesOptions"
                            v-model="selectedPolicy"
                            @input="removeFocus"
                            class="remove-focus-from-select"
                        ></form-select>
                        <responsive-button
                            class="remove-focus-from-button"
                            variant="primary"
                            :icon="['fas', 'pen-to-square']"
                            @click="openModal({ edit: true })"
                        ></responsive-button>
                        <responsive-button
                            class="remove-focus-from-button"
                            v-if="!isDefault"
                            variant="primary"
                            :icon="['fas', 'minus']"
                            @click="$bvModal.show('delete-policy' + selectedPolicy)"
                        ></responsive-button>
                        <responsive-button
                            class="remove-focus-from-button"
                            variant="primary"
                            :icon="['fas', 'copy']"
                            @click="openModal({ clone: true })"
                        ></responsive-button>
                        <responsive-button
                            class="remove-focus-from-button"
                            variant="primary"
                            :icon="['fas', 'plus']"
                            @click="openModal({ edit: false })"
                        ></responsive-button>
                    </div>
                    <div class="small">
                        <b>Children</b>
                        {{ getPermission(currentPolicy && currentPolicy.children) }}
                        / <b>Events</b>
                        {{ getPermission(currentPolicy && currentPolicy.events) }}
                        / <b>Smoking</b>:
                        {{ getPermission(currentPolicy && currentPolicy.smoking) }}
                        / <b>Pets</b>:
                        {{ getPermission(currentPolicy && currentPolicy.pets) }}
                        / <b>Min Renting Age</b>:
                        {{ (currentPolicy && currentPolicy.minimum_renting_age) || 'Disabled' }}
                    </div>
                </div>
            </grid>
        </div>

        <validation-observer ref="policy_form" v-slot="{ meta, setFieldValue, errors }">
            <modal
                :id="editMode ? editModalId : cloneMode ? cloneModalId : 'create-policy'"
                :title="editMode ? 'Edit Policy' : cloneMode ? 'Clone Policy' : 'Add Policy'"
                ok-title="Save"
                :ok-disabled="!meta.valid"
                @ok.prevent="createUpdatePolicy"
                @hidden="onHideModal"
                @shown="setData"
            >
                <div>
                    <validation-provider name="policy_name" v-slot="{ errors }" rules="required" >
                        <form-input
                            v-model="policyFormObject.name"
                            label="Policy Name:"
                            :error="errors[0]"
                            @input="(val) => setFieldValue('policy_name', val)"
                        ></form-input>
                    </validation-provider>
                </div>
                <div class="d-flex">
                    <div class="w-100 toggle-item">
                        Children:
                        <form-square-switch
                            v-model="policyFormObject.children"
                            :sync="true"
                            :labels="{
                                checked: 'ON',
                                unchecked: 'OFF',
                            }"
                            :color="{
                                unchecked: 'rgba(0,0,0,.6)',
                            }"
                        ></form-square-switch>
                    </div>
                    <div class="w-100 toggle-item">
                        Events:
                        <form-square-switch
                            v-model="policyFormObject.events"
                            :sync="true"
                            :labels="{
                                checked: 'ON',
                                unchecked: 'OFF',
                            }"
                            :color="{
                                unchecked: 'rgba(0,0,0,.6)',
                            }"
                        ></form-square-switch>
                    </div>
                    <div class="w-100 toggle-item">
                        Smoking:
                        <form-square-switch
                            v-model="policyFormObject.smoking"
                            :sync="true"
                            :labels="{
                                checked: 'ON',
                                unchecked: 'OFF',
                            }"
                            :color="{
                                unchecked: 'rgba(0,0,0,.6)',
                            }"
                        ></form-square-switch>
                    </div>
                    <div class="w-100 toggle-item">
                        Pets:
                        <form-square-switch
                            v-model="policyFormObject.pets"
                            :sync="true"
                            :labels="{
                                checked: 'ON',
                                unchecked: 'OFF',
                            }"
                            :color="{
                                unchecked: 'rgba(0,0,0,.6)',
                            }"
                        ></form-square-switch>
                    </div>
                </div>
                <div class="mt-3" v-if="policyFormObject.pets">
                    <form-textarea label="Pets Policy:" v-model="policyFormObject.pet_policy"> </form-textarea>
                    <form-select
                        label="Maximum Pets:"
                        class="mt-2"
                        :options="maxPetsOptions"
                        v-model="policyFormObject.maximum_pets"
                    >
                    </form-select>
                </div>
                <form-select
                    label="Minimum Renting Age:"
                    class="mt-3"
                    :options="rentingAgeOptions"
                    v-model="policyFormObject.minimum_renting_age"
                >
                </form-select>
            </modal>
        </validation-observer>
        <modal :id="'delete-policy' + selectedPolicy" title="Warning" @ok.prevent="removePolicy">
            Are you sure you want to delete this Policy? All associations with properties will be lost.
        </modal>
    </page>
</template>

<script>
    import FormSelect from '../generic/form-select/form-select.vue';
    import page from '../generic/page/page.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import {
        POLICIES_DATA_ENDPOINT,
        POLICIES_CREATE_POLICY_ENDPOINT,
        POLICIES_DELETE_POLICY_ENDPOINT,
        POLICIES_CLONE_POLICY_ENDPOINT,
        POLICIES_SET_PROPERTY_ENDPOINT,
    } from '../../endpoints';
    import API from '../../api';
    import Grid from '../generic/grid/grid.vue';
    import Modal from '../generic/modal/modal.vue';
    import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
    import FormInput from '../generic/form-input/form-input.vue';
    import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    export default {
        components: {
            page,
            FormSelect,
            ResponsiveButton,
            Grid,
            Modal,
            FormSquareSwitch,
            FormInput,
            FormTextarea,
        },
        name: 'policiesList',
        data() {
            return {
                tabs: [
                    {
                        id: 'policies',
                        title: 'Policies',
                        active: true,
                        route: '/app/policy/list',
                    },
                    {
                        id: 'cancellation_policies',
                        title: 'Cancellation Policies',
                        active: false,
                        route: '/app/policy/cancellation-policy/list',
                    },
                    {
                        id: 'amenities_manager',
                        title: 'Amenities',
                        active: false,
                        route: '/app/policy/amenity-manager/list',
                    },
                    {
                        id: 'meta_data_manager',
                        title: 'Amenity Metadata',
                        active: false,
                        route: '/app/policy/meta-data/list',
                    },
                ],
                busy: null,
                selectedPolicy: null,
                policiesOptions: [],
                policiesTemplates: [],
                offset: 0,
                limit: 20,
                sort: 'id',
                desc: false,
                search: '',
                editMode: false,
                cloneMode: false,
                policyFormObject: {
                    children: true,
                    default: null,
                    events: null,
                    id: null,
                    maximum_pets: 1,
                    minimum_renting_age: 'Disabled',
                    name: null,
                    pet_policy: null,
                    pets: null,
                    smoking: null,
                },
            };
        },
        mounted() {
            this.fetchPolicies();
        },
        methods: {
            onHideModal() {
                this.editMode = null;
                this.cloneMode = null;
                this.$nextTick(() => {
                    document.querySelectorAll('.remove-focus-from-button').forEach((item) => item.blur());
                });
            },
            removeFocus(e) {
                document.querySelector('.remove-focus-from-select .custom-select').blur();
            },
            async createUpdatePolicy() {
                try {
                    const { data } = await API.post(
                        this.editMode
                            ? POLICIES_CREATE_POLICY_ENDPOINT(this.selectedPolicy)
                            : this.cloneMode
                            ? POLICIES_CLONE_POLICY_ENDPOINT(this.policyFormObject.id)
                            : POLICIES_CREATE_POLICY_ENDPOINT(),
                        { ...this.policyFormObject, clone: this.cloneMode }
                    );
                    await this.fetchPolicies();
                    this.$bvModal.hide(
                        this.editMode ? this.editModalId : this.cloneMode ? this.cloneModalId : 'create-policy'
                    );
                    this.selectedPolicy = this.policiesOptions.find((item) => item.value === data.template.id).value;
                    this.$refs.grid.refreshDataSource();
                } catch (e) {
                    this.errorToast({
                        text: `Could not ${this.editMode ? 'update' : 'create'} policy`,
                    });
                }
            },
            openModal(params) {
                if (params.edit) {
                    this.editMode = true;
                    this.policyFormObject = { ...this.currentPolicy };
                    this.policyFormObject.minimum_renting_age =
                        this.policyFormObject.minimum_renting_age || this.rentingAgeOptions[0].value;
                    this.$nextTick(() => {
                        this.$bvModal.show(this.editModalId);
                    });
                } else if (params.clone) {
                    this.cloneMode = true;
                    this.policyFormObject = {
                        ...this.currentPolicy,
                        name: this.currentPolicy.name + ' Clone',
                    };
                    this.policyFormObject.minimum_renting_age =
                        this.policyFormObject.minimum_renting_age || this.rentingAgeOptions[0].value;
                    this.$nextTick(() => {
                        this.$bvModal.show(this.cloneModalId);
                    });
                } else if (!params.edit) {
                    this.editMode = false;
                    this.cloneMode = false;
                    this.policyFormObject = {
                        children: true,
                        default: false,
                        events: false,
                        id: 0,
                        maximum_pets: 1,
                        minimum_renting_age: this.rentingAgeOptions[0].value,
                        name: null,
                        pet_policy:
                            'Please enter your pet policy here. This policy will be displayed to the potential guest during the online booking process if wish to bring a pet.',
                        pets: false,
                        smoking: false,
                    };
                    this.$nextTick(() => {
                        this.$bvModal.show('create-policy');
                    });
                }
            },
            async removePolicy() {
                try {
                    await API.post(POLICIES_DELETE_POLICY_ENDPOINT(this.selectedPolicy));
                    this.$bvModal.hide('delete-policy' + this.selectedPolicy);
                    await this.fetchPolicies();
                    this.$refs.grid.refreshDataSource();
                    this.successToast({ text: 'Policy successfuly deleted' });
                } catch (e) {
                    this.errorToast({ text: 'Could not delete policy' });
                }
            },
            async fetchPolicies() {
                try {
                    this.busy = true;
                    const { data } = await API.get(POLICIES_DATA_ENDPOINT());
                    this.policiesTemplates = data.policy_templates;
                    this.policiesOptions = data.policy_templates.map((item) => {
                        return {
                            value: item.id,
                            text: item.name,
                            default: item.default,
                        };
                    });
                    this.selectedPolicy = this.policiesOptions.find((item) => item.default).value;
                } catch (e) {
                    this.errorToast({ text: 'Could not load data' });
                } finally {
                    this.busy = false;
                }
            },
            getPermission(value) {
                return value ? 'Allowed' : 'Forbidden';
            },
            getPolicyName({ data }) {
                if ({ data }) {
                    if (data.policy_template) {
                        return `${data.policy_template.name}`;
                    }
                }
            },
            setData() {
                this.$refs.policy_form.setValues({
                    policy_name: this.policyFormObject.name,
                });
            },
        },
        computed: {
            editModalId() {
                return 'edit-policy' + this.selectedPolicy;
            },
            cloneModalId() {
                return 'clone-policy' + this.selectedPolicy;
            },
            rentingAgeOptions() {
                let arr = [];
                for (let i = 18; i <= 99; i++) {
                    arr.push(i);
                }
                let newArr = arr.map((item) => ({
                    value: item,
                    text: item,
                }));
                newArr.unshift({ value: 0, text: 'Disabled' });
                return newArr;
            },
            maxPetsOptions() {
                return Array.from({ length: 5 }, (_, i) => i + 1).map((item) => ({
                    value: item,
                    text: item,
                }));
            },
            columnDefs() {
                return [
                    {
                        field: 'id',
                        headerName: 'Lodgix ID',
                        maxWidth: 120,
                        sort: 'asc',
                    },
                    {
                        field: 'name',
                        headerName: 'Property',
                    },
                    {
                        field: 'policies.children',
                        headerName: 'Children',
                        type: 'yesNoColumn',
                        maxWidth: 120,
                    },
                    {
                        field: 'policies.events',
                        headerName: 'Events',
                        type: 'yesNoColumn',
                        maxWidth: 120,
                    },
                    {
                        field: 'policies.smoking',
                        headerName: 'Smoking',
                        type: 'yesNoColumn',
                        maxWidth: 120,
                    },
                    {
                        field: 'policies.pets',
                        headerName: 'Pets',
                        type: 'yesNoColumn',
                        maxWidth: 120,
                    },
                    {
                        field: 'policies.maximum_pets',
                        headerName: 'Max Pets',
                        maxWidth: 120,
                        valueGetter: (params) => {
                            if (params.data) {
                                const { pets, maximum_pets } = params.data.policies;
                                return params.data && (pets ? maximum_pets : 'N/A');
                            }
                        },
                    },
                    {
                        field: 'policies.pet_policy',
                        headerName: 'Pet policy',
                        valueGetter: (params) => {
                            if (params.data) {
                                const { pets, pet_policy } = params.data.policies;
                                return params.data && (pets ? pet_policy : 'N/A');
                            }
                        },
                    },
                    {
                        field: 'policies.minimum_renting_age',
                        headerName: 'Min Renting Age',
                        maxWidth: 165,
                        minWidth: 165,
                        valueGetter: (params) => {
                            if (params.data) {
                                const { minimum_renting_age } = params.data.policies;
                                return minimum_renting_age ? minimum_renting_age : 'Disabled';
                            }
                        },
                    },
                    {
                        field: 'policy_template',
                        colId: 'policy_template.name',
                        headerName: 'Policy',
                        cellEditor: 'selectCellEditor',
                        editable: true,
                        cellClass: 'text-primary lx-editable-cell',
                        getQuickFilterText: (params) => {
                            if (params.data) {
                                return params.data.policy_template.name;
                            }
                        },
                        cellEditorParams: () => ({
                            options: this.policiesOptions,
                        }),
                        cellRenderer: (params) => params.data && this.getPolicyName(params),
                        valueGetter: (params) => {
                            if (params.data) {
                                return params.data.policy_template && params.data.policy_template.id;
                            }
                        },
                        valueSetter: async ({ node, data, newValue, oldValue, api }) => {
                            (async () => {
                                try {
                                    await API.post(POLICIES_SET_PROPERTY_ENDPOINT(newValue, data.id));
                                    setTimeout(function () {
                                        api.redrawRows();
                                    }, 0);
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not update property',
                                    });
                                    data.policy_template = this.policiesTemplates.find((p) => p.id === oldValue);
                                    node.setData(data);
                                }
                            })();
                            const policy = this.policiesTemplates.find((p) => p.id === newValue);
                            data.policy_template = policy;
                            data.policies = { ...policy };
                            return true;
                        },
                    },
                ];
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get('/app/property/property/policy-grid/', {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                        },
                    });
                };
            },
            isDefault() {
                return this.policiesTemplates.some((item) => item.id === this.selectedPolicy && item.default);
            },
            currentPolicy() {
                return this.policiesTemplates.find((item) => item.id === this.selectedPolicy);
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(.select-policy) {
        padding-top: 10px;
        .ag-cell-value {
            width: 100%;
        }
    }
    .policy-list {
        height: 100%;
    }

    @include media-breakpoint-down(xl) {
        .policy-list {
            overflow: hidden;
            padding-bottom: 40px;
        }
    }

    @include media-breakpoint-down(md) {
        .policy-list {
            overflow: auto;
        }
    }

    .policy-toolbar {
        display: flex;
        align-items: center;
        .form-group {
            margin-bottom: 0;
            :deep(.custom-select) {
                border-top-right-radius: 0 !important;
                border-bottom-right-radius: 0 !important;
            }
        }
        .btn {
            margin-right: 0 !important;
            &:not(:last-child) {
                border-radius: 0;
            }
            &:last-child {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }
        }
    }
</style>

<style>
    .toggle-item .v-switch-label {
        padding-top: 0.2em !important;
    }

    .toggle-item .v-switch-core {
        height: 2.35em !important;
    }

    .toggle-item .v-switch-button {
        top: 0.18em !important;
        height: 2em !important;
    }
    .toggle-item {
        margin-right: 1rem !important;
    }
    @media (max-width: 360px) {
        .toggle-item .vue-js-switch {
            width: 65px !important;
        }
        .toggle-item .v-switch-label {
            font-weight: 600 !important;
            font-size: 14px !important;
        }
        .toggle-item {
            margin-right: 0.2rem !important;
        }
    }
</style>
